"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { loadPublicHomeBanners } from "@/shared/api/services/homeBannerService";
import { cn } from "@/lib/utils";

const AUTO_ADVANCE_MS = 5000;
const MD_BREAKPOINT = "(min-width: 768px)";

export function HeroBanner() {
  const { data: slides = [] } = useQuery({
    queryKey: ["home-banners"],
    queryFn: loadPublicHomeBanners,
    staleTime: 0,
  });
  const [page, setPage] = useState(0);
  const [perView, setPerView] = useState(2);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    const mql = window.matchMedia(MD_BREAKPOINT);
    const update = () => setPerView(mql.matches ? 2 : 1);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  const pageCount = Math.ceil(slides.length / perView);

  useEffect(() => {
    if (page >= pageCount) setPage(0);
  }, [pageCount, page]);

  const goTo = useCallback(
    (index: number) => {
      setPage((index + pageCount) % pageCount);
    },
    [pageCount],
  );

  useEffect(() => {
    if (isPaused || pageCount <= 1) return;
    timerRef.current = setInterval(
      () => setPage((prev) => (prev + 1) % pageCount),
      AUTO_ADVANCE_MS,
    );
    return () => clearInterval(timerRef.current);
  }, [isPaused, pageCount]);

  if (!slides.length) return null;

  return (
    <section
      aria-label="홈 배너"
      className="py-6 md:py-8"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-screen-2xl mx-auto px-4 relative">
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${page * 100}%)` }}
          >
            {Array.from({ length: pageCount }).map((_, pageIndex) => {
              const group = slides.slice(pageIndex * perView, pageIndex * perView + perView);
              return (
                <div key={pageIndex} className="flex gap-3 flex-shrink-0 w-full items-start">
                  {group.map((slide) => (
                    <Link
                      key={slide.id}
                      href={slide.ctaHref}
                      aria-label={slide.alt}
                      className={cn(
                        "relative rounded-lg overflow-hidden grow basis-0",
                        "aspect-[2.5/1]",
                        perView === 2 && "max-w-[calc(50%-6px)]",
                      )}
                    >
                      <Image
                        src={slide.imageUrl}
                        alt={slide.titleLines.length > 0 ? "" : slide.alt}
                        fill
                        unoptimized
                        sizes="(max-width: 768px) 100vw, 50vw"
                        priority={pageIndex === 0}
                        className="object-cover"
                      />
                      {(slide.eyebrow || slide.titleLines.length > 0 || slide.subtitle) && (
                        <div
                          className={cn(
                            "absolute inset-0 z-10 flex items-center pl-10 pr-5 sm:px-8 lg:px-10",
                            slide.textTone === "dark" ? "text-neutral-950" : "text-white",
                          )}
                        >
                          <div className="flex w-[58%] flex-col items-start sm:w-[54%]">
                            {slide.eyebrow && (
                              <span className="mb-2 rounded-full bg-white/15 px-2.5 py-1 text-[9px] font-semibold tracking-[0.12em] backdrop-blur-sm sm:mb-3 sm:px-3 sm:text-[10px] lg:text-xs">
                                {slide.eyebrow}
                              </span>
                            )}
                            {slide.titleLines.length > 0 && (
                              <span className="text-[clamp(1.1rem,2.05vw,2.25rem)] font-bold leading-[1.12] tracking-[-0.035em] drop-shadow-sm">
                                {slide.titleLines.map((line, index) => (
                                  <span key={index} className="block">
                                    {line}
                                  </span>
                                ))}
                              </span>
                            )}
                            {slide.subtitle && (
                              <span
                                className={cn(
                                  "mt-3 max-w-[95%] text-xs font-medium leading-relaxed lg:text-sm",
                                  slide.textTone === "dark"
                                    ? "text-neutral-800/80"
                                    : "text-white/80",
                                )}
                              >
                                {slide.subtitle}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {pageCount > 1 && (
          <>
            <button
              type="button"
              onClick={() => goTo(page - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md text-neutral-800 hover:bg-neutral-100 transition-colors"
              aria-label="이전"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(page + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-md text-neutral-800 hover:bg-neutral-100 transition-colors"
              aria-label="다음"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {pageCount > 1 && (
          <div className="flex justify-center gap-1.5 mt-4">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === page ? "bg-neutral-800 w-4" : "bg-neutral-300 w-1.5 hover:bg-neutral-400",
                )}
                aria-label={`페이지 ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
